import { axios } from '@seiue/axios'
import { Toast, Modal as SeiueModal } from '@seiue/ui'
import { isArray } from '@seiue/util'
import { Modal } from 'antd'
import { useAtomValue } from 'jotai'
import { RoleEnum } from 'packages/types'
import { env } from 'packages/utils'
import React, { useEffect, useCallback } from 'react'

import { interimSchoolIdAtom } from './atom'
import { ACTIVITY_SUBDOMAIN, DOMAIN_REGEX } from './const'
import {
  schoolIdStorage,
  isAdminStorage,
  reflectionStorage,
  xRbacTokenStorage,
  store,
} from './store'

/**
 * 获取当前登录用户信息
 *
 * @returns current reflection
 */
export const useCurrentReflection = () => {
  return useAtomValue(reflectionStorage)
}

/**
 * 是否是离校学生
 *
 * @returns 当前是否是离校学生
 */
export const useIsCurrentStudentArchived = () => {
  const reflection = useAtomValue(reflectionStorage)

  return Boolean(
    reflection?.archivedAt && reflection?.role === RoleEnum.Student
  )
}

/**
 * 是否是超管
 *
 * @returns 当前是否是超管
 */
export const useIsShadow = () => {
  const reflection = useAtomValue(reflectionStorage)

  return Boolean(reflection?.role === RoleEnum.Shadow)
}

/**
 * 获取当前学校id
 *
 * @returns current school id
 */
export const useCurrentSchoolId = () => {
  return useAtomValue(schoolIdStorage)
}

/**
 * 是否是教师管理界面
 *
 * @returns is admin
 */
export const useIsAdmin = () => {
  return useAtomValue(isAdminStorage)
}

// 常量定义
export const CONSTANTS = {
  ACTIVITY_SUBDOMAIN,
  LOGIN_PATH: 'login',
  ERROR_MESSAGES: {
    UNKNOWN: '未知错误',
    UNAUTHORIZED: 'Unauthorised',
    LOGIN_ERROR: '登录信息错误',
    LOGIN_EXPIRED: '登录已过期',
    LOGIN_INVALID: '您的登录状态已失效，请重新登录',
    SERVER_ERROR: '500: 未知错误',
    NO_PERMISSION: '当前角色无权限',
    DATA_UPDATED: '页面数据更新',
  },
  STATUS_CODES: {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    SERVER_ERROR: 500,
    GONE: 410,
  },
  URLS: {
    NOT_FOUND: '/404',
    LOGIN_BASE: '/login',
  },
  MESSAGE_CONFIG: {
    TOP_OFFSET: 64,
  },
} as const

/**
 * 从 URL 路径中提取纯数字的 schoolId
 * 处理 URL 编码的情况
 */
const extractNumericSchoolId = (str: string): string | undefined => {
  if (!str) return undefined

  // 先尝试 URL 解码
  let decoded: string
  try {
    decoded = decodeURIComponent(str)
  } catch {
    decoded = str
  }

  // 提取第一个连续的数字部分
  const match = decoded.match(/^(\d+)/)
  return match?.[1]
}

const getValidSchoolId = (id: unknown): string | undefined => {
  if (id === null || id === undefined) return undefined
  if (typeof id === 'number') return id.toString()
  if (typeof id === 'string') {
    const trimmed = id.trim()
    // 如果是字符串，尝试提取纯数字部分（处理 URL 编码的情况）
    return extractNumericSchoolId(trimmed) || trimmed
  }
  if (typeof id === 'object' && id !== null && 'id' in id) {
    const idValue = (id as any).id
    if (typeof idValue === 'string') {
      return extractNumericSchoolId(idValue) || String(idValue)
    }
    return String(idValue)
  }
  return undefined
}

/**
 * 工具函数
 *
 * @returns utils
 */
export const utils = {
  getSchoolUrl: (): string => {
    const match = location.hostname.match(DOMAIN_REGEX)
    return match?.[1] || ''
  },

  isActivity: (): boolean => {
    return utils.getSchoolUrl() === CONSTANTS.ACTIVITY_SUBDOMAIN
  },

  isLoginPage: (): boolean => {
    return location.pathname.includes(CONSTANTS.LOGIN_PATH)
  },

  getLoginUrl: (schoolId?: string, param?: string): string => {
    if (utils.isActivity()) {
      return `${CONSTANTS.URLS.LOGIN_BASE}?param=${param || ''}`
    }

    if (schoolId) {
      // 确保 schoolId 是纯数字
      const numericId = extractNumericSchoolId(schoolId)
      if (numericId && !isNaN(Number(numericId))) {
        return `${CONSTANTS.URLS.LOGIN_BASE}/${numericId}`
      }
    }

    return CONSTANTS.URLS.NOT_FOUND
  },

  extractErrorMessage: (data: any): string => {
    if (!data) return CONSTANTS.ERROR_MESSAGES.UNKNOWN

    if (isArray(data) && data.length > 0) {
      return data[0]?.message || CONSTANTS.ERROR_MESSAGES.UNKNOWN
    }

    return data.message || CONSTANTS.ERROR_MESSAGES.UNKNOWN
  },

  handleValidationError: (data: any[]): void => {
    SeiueModal.info({
      title: '错误提示',
      content: data.map((d, i) => <div key={i}>{d.message}</div>),
    })
  },
}

// 自定义Hook：处理活动参数
export const useActivityParam = () => {
  const [param, setParam] = React.useState('')
  const isActivity = utils.isActivity()
  const isLoginPage = utils.isLoginPage()

  useEffect(() => {
    const initActivityParam = async () => {
      if (!isActivity || isLoginPage) return

      const activityId = localStorage.getItem('activityId')
      if (!activityId || isNaN(Number(activityId))) return

      try {
        const response = await axios.get(
          `${env('SERVER_GROWP')}/urlhash/urlhashs`,
          {
            params: { activityId: Number(activityId) },
          }
        )
        setParam(response.data?.hash || '')
      } catch (error: any) {
        const data = error.reason?.response?.data
        const errMsg = utils.extractErrorMessage(data)
        Toast.warning(errMsg)
      }
    }

    initActivityParam()
  }, [isActivity, isLoginPage])

  return param
}

// 自定义Hook：错误处理
export const useErrorHandler = (param: string) => {
  const isActivity = utils.isActivity()
  const isLoginPage = utils.isLoginPage()

  const interimSchoolId = useAtomValue(interimSchoolIdAtom)

  const handleUnauthorizedError = useCallback(
    (errMsg: string, loginUrl: string): boolean => {
      if (isLoginPage) return false

      const reflection = store.get(reflectionStorage)
      if (!reflection) {
        window.location.href = loginUrl
        return false
      }

      Modal.warning({
        title: (
          <div>
            {errMsg === CONSTANTS.ERROR_MESSAGES.UNAUTHORIZED
              ? CONSTANTS.ERROR_MESSAGES.LOGIN_ERROR
              : errMsg || CONSTANTS.ERROR_MESSAGES.LOGIN_EXPIRED}
          </div>
        ),
        content: CONSTANTS.ERROR_MESSAGES.LOGIN_INVALID,
        onOk: () => {
          localStorage.setItem('user-preview', '')
          store.set(reflectionStorage, undefined)
          store.set(xRbacTokenStorage, undefined)
          window.location.href = loginUrl
        },
      })
      return false
    },
    [isLoginPage]
  )

  const handleStatusError = useCallback(
    (status: number, errMsg: string, schoolId?: string | number): boolean => {
      const loginUrl = utils.getLoginUrl(schoolId?.toString(), param)

      switch (status) {
        case CONSTANTS.STATUS_CODES.UNAUTHORIZED:
          return handleUnauthorizedError(errMsg, loginUrl)

        case CONSTANTS.STATUS_CODES.SERVER_ERROR:
          Toast.error(CONSTANTS.ERROR_MESSAGES.SERVER_ERROR)
          return false

        case CONSTANTS.STATUS_CODES.FORBIDDEN:
          if (isLoginPage) {
            Toast.warning(CONSTANTS.ERROR_MESSAGES.NO_PERMISSION)
          }
          return false

        case CONSTANTS.STATUS_CODES.NOT_FOUND:
          if (isActivity && isLoginPage) {
            location.href = CONSTANTS.URLS.NOT_FOUND
          }
          return true

        case CONSTANTS.STATUS_CODES.GONE:
          Toast.warning(errMsg || CONSTANTS.ERROR_MESSAGES.DATA_UPDATED)
          location.reload()
          return false

        default:
          Toast.warning(errMsg)
          return true
      }
    },
    [param, isActivity, isLoginPage, handleUnauthorizedError]
  )

  const handleUnhandledRejection = useCallback(
    (event: PromiseRejectionEvent): boolean => {
      event.preventDefault()

      const status = event.reason?.response?.status
      if (!status) return false

      const schoolId = getValidSchoolId(
        interimSchoolId || store.get(schoolIdStorage)
      )
      const data = event.reason?.response?.data
      const errMsg = utils.extractErrorMessage(data)

      // 处理验证错误
      if (
        data &&
        isArray(data) &&
        data.length > 0 &&
        data[0].type === 'validation'
      ) {
        utils.handleValidationError(data)
        return false
      }

      return handleStatusError(status, errMsg, schoolId)
    },
    [handleStatusError]
  )

  return { handleUnhandledRejection }
}
